import React, { useCallback, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { supabaseClient } from '../../config/supabase-client';
import { Panel } from "primereact/panel";
import { useGeneratorContext } from "../../context/GeneratorContext";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";

export const SearchThinkhausFieldsUpload = () => {
    const {
        products,
        uploadTableData, setUploadTableData,
        resultsView, setResultsView,
        isShouldFetchResults, setIsShouldFetchResults
    } = useGeneratorContext();

    const resultsViewOptions = [
        { name: "View All", value: "viewAll" },
        { name: "View Update", value: "viewUpdate" },
        { name: "View Publish", value: "viewPublish" },
    ];

    const errorFields = (error, index) => {
        return (
            error &&
            <div>
                <Panel className='w-15rem custom-error-panel' header="Error" collapsed={true} toggleable>
                    <p className="m-0 text-left no-wrap" data-testid={`fileInState-error-text-${index}`} >
                        {error}
                    </p>
                </Panel>
            </div>
        )
    }

    const fieldsField = (text, index) => {
        return (
            <div className="nowrap h-4">
                <p>
                    {text ? text?.fields.join(', ') : 'N/A'}
                </p>
            </div>
        )
    }

    const columnConfig = {
        viewAll: [
            { field: "upc", header: "UPC" },
            { field: "product_title", header: "Product Title" },
            { field: "email", header: "User Email" },
            {
                field: "date",
                header: "Date",
                body: (rowData) => {
                    const date = new Date(rowData?.date);
                    return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    });
                }
            },
            { field: "request_type", header: "Type of Request" },
            {
                field: "is_success",
                header: "Result",
                body: (rowData) => rowData?.is_success ? 'Success' : 'Failed'
            },
        ],
        viewUpdate: [
            { field: "upc", header: "UPC" },
            { field: "product_title", header: "Product Title" },
            { field: "email", header: "User Email" },
            {
                field: "date",
                header: "Date",
                body: (rowData) => {
                    const date = new Date(rowData?.date);
                    return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    });
                }
            },
            {
                field: "is_success",
                header: "Result",
                body: (rowData) => rowData?.is_success ? 'Success' : 'Failed'
            },
        ],
        viewPublish: [
            { field: "upc", header: "UPC" },
            { field: "product_title", header: "Product Title" },
            { field: "email", header: "User Email" },
            {
                field: "date",
                header: "Date",
                body: (rowData) => {
                    const date = new Date(rowData?.date);
                    return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    });
                }
            },
            {
                field: "is_success",
                header: "Result",
                body: (rowData) => rowData?.is_success ? 'Success' : 'Failed'
            },
        ],
    };

    const fetchResults = useCallback(async () => {
        const upcArray = products.map(product => product.UPC.toString()); // Extract UPCs from products
        
        let query = supabaseClient
            .from('syndigo_request_combined_view ')
            .select("*")
            .in('upc', upcArray);

        // console.log({resultsView})

        if (resultsView === "viewPublish") {
            query = query.filter("request_type", 'eq', 'publish');
        } 
        if (resultsView === "viewUpdate") {
            query = query.filter("request_type", 'eq', 'update');
        }

        // Fetch latest publish and update data by joining with thinkhaus_upload_upcs
        const { data, error } = await query;

        if (error) {
            console.error('Error fetching data:', error);
        } else {
            // Create a lookup map for faster access
            const dataMap = new Map(data.map(product => [product.upc, product]));

            // Map the upcArray to the sorted results using the dataMap for O(1) lookups
            const sortData = upcArray.map(upc => dataMap.get(upc) || { upc }); // use the upc if there was no matching data found

            setUploadTableData(sortData); // Set the combined results to be used by the DataTable

        }
    }, [products, setUploadTableData, resultsView]);

    const selectedColumns = columnConfig[resultsView];

    useEffect(() => {
        if (isShouldFetchResults) {
            fetchResults();
            setIsShouldFetchResults(false);
        }
    }, [products, fetchResults, isShouldFetchResults, setIsShouldFetchResults]);

    const handleResultsViewChange = (e: SelectButtonChangeEvent) => {
        console.log({e})
        setResultsView(e.value);
        setIsShouldFetchResults(true);
    }

    return (
        <div >
            <Panel header="Data Views" toggleable collapsed className='mb-2' data-testid='Data-Views-Panel'>
                <SelectButton className="flex justify-content-center" value={resultsView} options={resultsViewOptions} optionLabel="name" onChange={handleResultsViewChange} />
            </Panel>
            {
                uploadTableData &&
                <DataTable value={uploadTableData} rows={products.length} className="p-datatable-sm" scrollable scrollHeight="500px" data-testid='results-table'>
                    {selectedColumns.map((col) => (
                        <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                            body={col.body ? col.body : undefined} // Use custom body if defined
                            className="w-15rem max-h-2"
                            style={{
                                minWidth: '150px',  // Set the minimum width for the column
                                maxWidth: '250px'   // Optionally, set a max width if you want to limit it
                            }}
                            bodyStyle={{
                                height: '60px', // Set specific height for each cell
                                textAlign: 'left',
                                overflow: 'auto', // Handle overflow, or 'auto' for scrollbars
                                textOverflow: 'ellipsis', // Show ellipsis for overflowed text
                                maxHeight: '60px',
                                whiteSpace: 'normal'
                            }}
                        />
                    ))}
                </DataTable>
            }

        </div>
    );
}